/**
 * Helpers for the "List of MNO's" page.
 */

/** Grand total block returned by GET /mnos/ (sum across every MNO matching the current search/filters). */
export interface MnoGrandTotal {
  currency?: string | null;
  etopup_balance?: number | string | null;
  bundle_balance?: number | string | null;
}

export const GRAND_TOTAL_LABEL = "Σ Grand Total";

const EMPTY_CELL = "--";
const DEFAULT_CURRENCY = "AFA";

const formatAmount = (currency: string | null | undefined, value: number | string | null | undefined) => {
  const amount = Number(value);
  return `${currency || DEFAULT_CURRENCY} ${(Number.isFinite(amount) ? amount : 0).toFixed(2)}`;
};

/**
 * Converts the API's `grand_total` into one value per table column (keyed by column id),
 * ready to be rendered as the last row of the MNO table.
 * Returns undefined when the response has no grand total (e.g. empty list).
 */
export const buildMnoGrandTotalRow = (
  response?: { grand_total?: MnoGrandTotal | null } | null
): Record<string, string> | undefined => {
  const grandTotal = response?.grand_total;
  if (!grandTotal) return undefined;

  return {
    mno_name: GRAND_TOTAL_LABEL,
    contract_start_date: EMPTY_CELL,
    contract_end_date: EMPTY_CELL,
    etopup_balance: formatAmount(grandTotal.currency, grandTotal.etopup_balance),
    bundle_balance: formatAmount(grandTotal.currency, grandTotal.bundle_balance),
  };
};

/**
 * True only when no MNO exists at all: the list finished loading, returned zero records,
 * and no search/filter is applied (pagination does not count as a filter).
 */
export const hasNoMnosAdded = (
  recordCount: number | undefined,
  loading: boolean,
  searchParams: URLSearchParams
): boolean => {
  if (loading || recordCount === undefined || Number(recordCount) !== 0) return false;
  return Array.from(searchParams.keys()).every((key) => key === "page_number");
};
